import { isRejected } from '@reduxjs/toolkit';
import { toastReplaced, toastShown, toastDismissed } from './feedbackSlice';
import { FEEDBACK_RULES } from './feedbackCatalog';
import { TOAST_ACTION, TOAST_TONE } from './feedbackConstants';
import { readSuccessMessage } from '../../utils/errors';
import { mutationUnconfirmed } from '../connection/connectionSlice';
import {
  notificationReceived,
  notificationsStaleChecked,
  notificationStarted,
} from '../notifications/notificationsSlice';
import { NOTIFICATION_KINDS, NOTIFICATION_STATUS } from '../notifications/notificationsConstants';

const LIFECYCLE = /^(.*)\/(pending|fulfilled|rejected)$/;

const DEFAULT_ERROR = 'No se pudo completar la acción.';

export const UNCONFIRMED_MESSAGE =
  'No tuvimos respuesta del servidor. La acción puede haberse aplicado: revisa antes de reintentar.';

const OPEN_ACTIVITY = { label: 'Ver', type: TOAST_ACTION.OPEN_ACTIVITY };

/** The toast that opens with a job, so its outcome can take the same place. */
const jobToastId = (requestId) => `job-${requestId}`;

/** One slot per Activity Center entry: its start, its outcome, its stall. */
const jobKey = (entry) => `job|${entry.id}`;

const isFocused = (state, kind) => (state.feedback?.focusedKinds?.[kind] ?? 0) > 0;

const resolve = (value, action) => (typeof value === 'function' ? value(action) : value);

/**
 * Turns a thunk's lifecycle into toasts, following its rule in the catalog.
 * Exported with the rules as a parameter so tests can drive it with their own.
 */
export const handleThunkLifecycle = (action, dispatch, rules) => {
  const match = typeof action.type === 'string' && action.type.match(LIFECYCLE);
  if (!match) return;

  const [, prefix, phase] = match;
  const rule = rules[prefix];
  if (!rule || action.meta?.arg?.feedback === false) return;

  const { requestId } = action.meta ?? {};
  const testId = rule.testId;

  if (phase === 'pending') {
    if (rule.pending) {
      dispatch(toastShown({ id: requestId, tone: TOAST_TONE.PROGRESS, message: resolve(rule.pending, action), testId }));
    }
    return;
  }

  if (phase === 'fulfilled') {
    const success = rule.success === 'response'
      ? readSuccessMessage(action.payload)
      : resolve(rule.success, action);
    // A rule may say more than a sentence: a partial outcome is a warning, and
    // a PDF the browser would not open comes with the button that opens it.
    const outcome = typeof success === 'string' ? { message: success } : success;

    if (outcome?.message) {
      const toast = { tone: TOAST_TONE.SUCCESS, testId, ...outcome };
      dispatch(rule.pending ? toastReplaced({ id: requestId, ...toast }) : toastShown(toast));
    } else if (rule.pending) {
      dispatch(toastDismissed(requestId));
    }
    return;
  }

  // An aborted or skipped thunk did not fail; it did not run. One that got no
  // answer may have worked: the interceptor's "puede haberse aplicado" says it,
  // and an error here would contradict it.
  if (action.meta?.aborted || action.meta?.condition || action.meta?.unconfirmed) {
    if (rule.pending) dispatch(toastDismissed(requestId));
    return;
  }

  if ((rule.error ?? 'toast') === 'toast') {
    const message = (typeof action.payload === 'string' && action.payload) || DEFAULT_ERROR;
    const toast = { tone: TOAST_TONE.ERROR, message, testId: rule.errorTestId ?? testId };
    dispatch(rule.pending ? toastReplaced({ id: requestId, ...toast }) : toastShown(toast));
  } else if (rule.pending) {
    dispatch(toastDismissed(requestId));
  }
};

const OUTCOME_TONES = {
  [NOTIFICATION_STATUS.SUCCESS]: TOAST_TONE.SUCCESS,
  [NOTIFICATION_STATUS.PARTIAL]: TOAST_TONE.WARNING,
  [NOTIFICATION_STATUS.ERROR]: TOAST_TONE.ERROR,
  [NOTIFICATION_STATUS.STALLED]: TOAST_TONE.WARNING,
};

/**
 * Toasts for the jobs that changed into an outcome between two states: the
 * Activity Center records it, this makes sure the user hears it on whatever
 * page they are now.
 */
export const announceJobOutcomes = (before, after, dispatch) => {
  const previous = new Map(before.notifications.items.map((item) => [item.id, item]));

  after.notifications.items.forEach((item) => {
    const tone = OUTCOME_TONES[item.status];
    if (!tone || previous.get(item.id)?.status === item.status) return;
    // A screen already showing this job in full says it better than a toast.
    if (isFocused(after, item.kind)) return;

    dispatch(
      toastShown({
        tone,
        title: item.title,
        message: item.message,
        action: item.url
          ? { label: 'Descargar archivo', type: TOAST_ACTION.OPEN_URL, url: item.url }
          : OPEN_ACTIVITY,
        dedupeKey: jobKey(item),
      })
    );
  });
};

export const registerFeedbackListeners = (startListening, rules = FEEDBACK_RULES) => {
  startListening({
    predicate: (action) => typeof action.type === 'string' && LIFECYCLE.test(action.type),
    effect: (action, { dispatch }) => handleThunkLifecycle(action, dispatch, rules),
  });

  startListening({
    actionCreator: notificationStarted,
    effect: (action, { dispatch, getState }) => {
      const { kind, requestId } = action.payload;
      const catalog = NOTIFICATION_KINDS[kind];
      if (catalog?.startToast === false) return;

      // The entry the reducer just opened. Sharing its key with the outcome
      // toast lets "Reporte listo" take the place of "En curso".
      const entry = getState().notifications.items[0];

      dispatch(
        toastShown({
          id: requestId ? jobToastId(requestId) : undefined,
          tone: TOAST_TONE.INFO,
          title: catalog?.title ?? kind,
          message: 'En curso. Te avisamos en el Centro de Actividad cuando termine.',
          action: OPEN_ACTIVITY,
          dedupeKey: entry ? jobKey(entry) : undefined,
        })
      );
    },
  });

  // The request that starts a job failed: its "en curso" becomes the error.
  // A request that got no answer is left to the unconfirmed warning below.
  startListening({
    matcher: isRejected,
    effect: (action, { dispatch, getOriginalState, getState }) => {
      const { requestId } = action.meta ?? {};
      const before = getOriginalState().notifications.items.find(
        (item) => item.requestId === requestId
      );
      const after = getState().notifications.items.find((item) => item.requestId === requestId);
      if (!before || !after || before.status === after.status) return;

      if (after.status !== NOTIFICATION_STATUS.ERROR) {
        dispatch(toastDismissed(jobToastId(requestId)));
        return;
      }
      // A screen showing this job is showing its failure too, like the
      // upload form. Once the user has left it, the toast is the only news.
      if (isFocused(getState(), after.kind)) return;

      dispatch(
        toastReplaced({
          id: jobToastId(requestId),
          tone: TOAST_TONE.ERROR,
          title: after.title,
          message: after.message,
          action: OPEN_ACTIVITY,
          dedupeKey: jobKey(after),
        })
      );
    },
  });

  startListening({
    predicate: (action) =>
      notificationReceived.match(action) || notificationsStaleChecked.match(action),
    effect: (action, { dispatch, getOriginalState, getState }) =>
      announceJobOutcomes(getOriginalState(), getState(), dispatch),
  });

  startListening({
    actionCreator: mutationUnconfirmed,
    effect: (action, { dispatch }) => {
      dispatch(
        toastShown({
          tone: TOAST_TONE.WARNING,
          message: UNCONFIRMED_MESSAGE,
          testId: 'action-unconfirmed-notice',
        })
      );
    },
  });
};
